import { AlertTriangle, Check, Loader2, X } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { useI18n } from '@/lib/i18n';
import type { AgentRow } from '@/lib/agents-api';

/**
 * CO SIĘ STAŁO PO KLIKNIĘCIU „WYŁĄCZ REPLIKACJĘ SPS-A".
 *
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *  ⚠️⚠️ POWSTAŁO Z ZGŁOSZENIA (03.09.2026), I TO Z DWÓCH NARAZ:
 *
 *    „w logach mamy tylko, że poszło, ale na stronie nie ma nic, że cokolwiek się udało"
 *    „SPS po tym się nie włączył co robiliśmy na małpkomacie"
 *
 *  Audyt notował ZLECENIE, a wynik zadania leżał w `machine_task.output` i nie miał gdzie się
 *  pokazać. Człowiek, który właśnie kliknął czerwony przycisk, nie miał żadnej drogi dowiedzieć
 *  się ani tego, że baza jest wyczyszczona, ani tego, że AUTOMAT STOI. To drugie jest tu
 *  najważniejsze: maszyna z wyłączonym SPSUI nie wydaje nic, a przy niej nikt tego nie wie.
 *
 *  ⚠️ POKAZUJEMY KROKI, NIE „OK/BŁĄD". Procedura ma pięć kroków na dwóch różnych warstwach (baza,
 *  dysk, proces) i każdy może zawieść osobno — a naprawa każdego jest inna. Jedna plakietka
 *  kazałaby zaglądać na maszynę, żeby ustalić, co właściwie się nie udało.
 *
 *  ⚠️ PORAŻKA STARTU INTERFEJSU JEST GŁOŚNA, nie jednym z pięciu wierszy: to jedyny skutek tej
 *  procedury, który zatrzymuje wydawanie u klienta.
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 */

type Krok = { krok: string; zrobione: boolean; szczegol: string };

type Wynik = {
  kroki?: Krok[];
  przed?: { triggerow?: number; wierszy?: number; naBazie?: boolean; tabel?: number };
  po?: { triggerow?: number; wierszy?: number; naBazie?: boolean; tabel?: number };
  wylaczone?: boolean;
  interfejsWystartowal?: boolean | null;
  czyszczenie?: { tabela: string; metoda: string; usunietych: number; partii: number }[];
  /** `CT_WYLACZ`: którą drogą poszło wyłączenie Change Trackingu. */
  droga?: 'NASZE_KONTO' | 'NARZEDZIE_SPS' | 'NIE_BYLO_CZEGO';
};

/** Krok po kroku, nazwami zrozumiałymi bez zaglądania w kod agenta. */
const NAZWY: Record<string, string> = {
  SPSUI_STOP: 'Zatrzymanie interfejsu automatu',
  ZDJECIE_REPLIKACJI: 'Zdjęcie triggerów (spsdbupdate)',
  UPRAWNIENIA: 'Prawo do opróżnienia tabel',
  PROGRAMY: 'Usunięcie programów kanału',
  SPSUI_START: 'Uruchomienie interfejsu automatu',
  TABELE: 'Wyczyszczenie tabel',
};

export function WynikWylaczeniaSpsSync({
  zadanie,
  juzWylaczone,
}: {
  zadanie: NonNullable<AgentRow['wylaczenieSpsSync']>;
  /**
   * Czy BIEŻĄCY pomiar mówi, że mechanizm jest już wyłączony.
   *
   * ⚠️⚠️ ISTNIEJE Z POWODU ZGŁOSZENIA (03.09.2026): „mam tu taki wiszący error czerwony". Zadanie
   * porzucone albo przerwane zostaje `FAILED` na zawsze, a robota w bazie mogła się już udać —
   * u klienta udała się w całości, a mimo to wisiał czerwony napis bez żadnej akcji do wykonania.
   * Bieżący pomiar wie lepiej niż stary wynik zadania i to on ma rozstrzygać ton komunikatu.
   */
  juzWylaczone?: boolean;
}) {
  const { t } = useI18n();
  const wToku = zadanie.status === 'QUEUED' || zadanie.status === 'SENT' || zadanie.status === 'RUNNING';
  const w = (zadanie.output ?? null) as Wynik | null;
  /**
   * ⚠️ JEDEN KOMPONENT NA OBA ZADANIA, rozróżniane po `kind`. Dwa niemal identyczne widoki
   * rozjechałyby się przy pierwszej poprawce — a różnią się DWIEMA rzeczami: tytułem i tym,
   * że wyłączenie CT nie tyka interfejsu automatu, więc nie ma tam czego ostrzegać.
   */
  const ct = zadanie.kind === 'CT_WYLACZ';

  /**
   * ⚠️ START INTERFEJSU CZYTAMY Z KROKÓW, A NIE Z SAMEGO `status`. Zadanie może być `FAILED`
   * właśnie z tego powodu — i wtedy cała robota w bazie jest zrobiona, a jedyne, co trzeba
   * naprawić, to podnieść SPSUI. Zlanie tego w „nie udało się" kazałoby powtarzać całość.
   */
  const start = w?.kroki?.find((k) => k.krok === 'SPSUI_START') ?? null;
  /** ⚠️ Tylko wyłączenie replikacji SPS-a tyka interfejsu — przy CT ten stan nie istnieje. */
  const automatStoi = !ct && zadanie.status === 'FAILED' && /SPSUI/i.test(zadanie.error ?? '');

  return (
    <div className="mt-2 space-y-1.5 rounded border border-border bg-muted/20 p-2 text-[11px] leading-snug">
      <p className="flex flex-wrap items-center gap-2">
        <span className="font-medium">{t(ct ? 'sledz.wynikCtTytul' : 'sledz.wynikTytul')}</span>
        {wToku ? (
          <Badge variant="neutral">
            <Loader2 className="mr-1 inline size-3 animate-spin" />
            {zadanie.note ?? t('common.inProgress')}
            {zadanie.progress === null ? '' : ` · ${zadanie.progress}%`}
          </Badge>
        ) : zadanie.status === 'DONE' ? (
          <Badge variant="success">{t('sledz.wynikGotowe')}</Badge>
        ) : juzWylaczone === true ? (
          /** ⚠️ Zadanie padło, ale mechanizmu już nie ma — plakietka nie może krzyczeć „błąd". */
          <Badge variant="neutral">{t('sledz.wynikPrzerwane')}</Badge>
        ) : (
          <Badge variant="destructive">{t('sledz.wynikBlad')}</Badge>
        )}
        <span className="text-muted-foreground">
          {new Date(zadanie.finishedAt ?? zadanie.createdAt).toLocaleString('pl-PL')}
        </span>
      </p>

      {/*
        ⚠️ NAJGŁOŚNIEJSZA RZECZ NA TYM BLOKU. Automat z wyłączonym SPSUI nie wydaje nic, a to
        jedyny skutek tej procedury, który człowiek musi naprawić NATYCHMIAST.
      */}
      {automatStoi && (
        <p className="flex items-start gap-1.5 rounded border border-[var(--destructive)] bg-[var(--destructive)]/10 p-1.5 font-medium text-[var(--destructive)]">
          <AlertTriangle className="mt-0.5 size-3.5 shrink-0" />
          <span>{t('sledz.automatStoi')}</span>
        </p>
      )}

      {/*
        ⚠️⚠️ „PRZERWANE, ALE ZROBIONE" TO OSOBNY, SPOKOJNY STAN. Bez tego czerwony błąd wisiał nad
        mechanizmem, którego już nie ma — a człowiek nie miał ani co kliknąć, ani jak sprawdzić,
        że jest w porządku. Pomiar rozstrzyga, nie stary wynik zadania.
      */}
      {zadanie.status === 'FAILED' && juzWylaczone === true && !automatStoi && (
        <p className="text-[var(--success)]">{t('sledz.wynikPrzerwaneAleZrobione')}</p>
      )}

      {/* Powód w całości — to jedyna droga, którą widać, co stało się na maszynie. */}
      {zadanie.error !== null && !automatStoi && (
        <p className={juzWylaczone === true ? 'text-muted-foreground' : 'text-[var(--destructive)]'}>
          {zadanie.error}
        </p>
      )}
      {automatStoi && <p className="text-muted-foreground">{zadanie.error}</p>}

      {/* Liczby przed i po — inne dla każdego z dwóch mechanizmów, bo mierzą inne rzeczy. */}
      {w?.przed !== undefined && w.po !== undefined && !ct && (
        <p className="text-muted-foreground">
          {t('sledz.wynikLiczby')
            .replace('{tPrzed}', String(w.przed.triggerow ?? '—'))
            .replace('{tPo}', String(w.po.triggerow ?? '—'))
            .replace('{wPrzed}', (w.przed.wierszy ?? 0).toLocaleString('pl-PL'))
            .replace('{wPo}', (w.po.wierszy ?? 0).toLocaleString('pl-PL'))}
        </p>
      )}
      {w?.przed !== undefined && w.po !== undefined && ct && (
        <p className="text-muted-foreground">
          {t('sledz.wynikCtLiczby')
            .replace('{tPrzed}', String(w.przed.tabel ?? '—'))
            .replace('{tPo}', String(w.po.tabel ?? '—'))
            .replace('{bPrzed}', t(w.przed.naBazie === true ? 'common.tak' : 'common.nie'))
            .replace('{bPo}', t(w.po.naBazie === true ? 'common.tak' : 'common.nie'))}
        </p>
      )}

      {/*
        ⚠️ KTÓRĄ DROGĄ POSZŁO WYŁĄCZENIE CT — po tym widać, czy nasze konto ma na tej maszynie
        prawo zmieniać strukturę, czy robotę wykonało narzędzie SPS-a swoim kontem.
      */}
      {ct && w?.droga === 'NARZEDZIE_SPS' && <p className="text-muted-foreground">{t('sledz.wynikCtNarzedziem')}</p>}

      {w?.kroki !== undefined && w.kroki.length > 0 && (
        <ul className="space-y-0.5">
          {w.kroki.map((k) => (
            <li key={k.krok} className="flex items-start gap-1.5">
              {k.zrobione ? (
                <Check className="mt-0.5 size-3 shrink-0 text-[var(--success)]" />
              ) : (
                <X className="mt-0.5 size-3 shrink-0 text-[var(--warning)]" />
              )}
              <span>
                <span className="font-medium">{NAZWY[k.krok] ?? k.krok}</span>
                <span className="text-muted-foreground"> — {k.szczegol}</span>
              </span>
            </li>
          ))}
        </ul>
      )}

      {/*
        ⚠️ KTÓRĄ DROGĄ POSZŁO CZYSZCZENIE — po tym widać, czy na tej maszynie nasze konto dostało
        prawo do `TRUNCATE`, czy zjechało na `DELETE` partiami. Bez tego różnica jest niewidoczna:
        tabela pusta, zadanie udane, a trwało minuty zamiast ułamka sekundy.
      */}
      {w?.czyszczenie !== undefined && w.czyszczenie.some((c) => c.metoda === 'DELETE') && (
        <p className="text-[var(--warning)]">{t('sledz.wynikDelete')}</p>
      )}

      {!ct && start !== null && start.zrobione && w?.wylaczone === true && (
        <p className="text-muted-foreground">{t('sledz.wynikPelne')}</p>
      )}
    </div>
  );
}
